import { useNavigateWithSearchParams } from "@p0security/frontend-core";
import type { InstallProps } from "@p0security/integrations-installer";
import { NEW_SENTINEL } from "@p0security/integrations-installer/Install/Component";
import { integrationRegistry } from "@p0security/shared/integrations/registry";
import type { RegisteredPermissionType } from "@p0security/shared/types/permission";
import { Spacing } from "@p0security/ui";
import { Page } from "components/App/Page";
import { once } from "lodash";
import { useCallback } from "react";
import { useParams } from "react-router-dom";

import { IntegrationsContextProvider } from "../../Integrations/IntegrationsContext.tsx";
import {
  IntegrationInstaller,
  installerRegistry,
} from "../../Integrations/installers.tsx";
import { WelcomePaths, useWelcomePath } from "../paths";
import { StyledCloudContainer } from "../styles";
import { CloudProviderParams } from "../types";

const cardOverride = {
  showBreadcrumbs: false,
};

const ProviderComponent: React.FC<
  Pick<
    InstallProps<any>,
    "cardOverride" | "componentOverride" | "onInstall"
  > & {
    id: RegisteredPermissionType;
  }
> = ({ id, cardOverride, componentOverride, onInstall }) => (
  <IntegrationsContextProvider>
    <Page title={integrationRegistry[id].label}>
      <IntegrationInstaller
        id={id}
        installer={installerRegistry[id]}
        registration={integrationRegistry[id]}
        cardOverride={cardOverride}
        componentOverride={componentOverride}
        // The submit callback in Config.tsx runs 3 times for unknown reasons.
        // That causes the navigate to run 3 times, clogging the browser history.
        // Take a defensive approach here until that is resolved.
        onInstall={onInstall && once(onInstall)}
      />
    </Page>
  </IntegrationsContextProvider>
);

export const CloudProviderContainer: React.FC = () => {
  const navigateWithSearchParams = useNavigateWithSearchParams();
  const params = useParams<CloudProviderParams>();
  const { cloudProvider, component } = params;

  const buildWelcomePath = useWelcomePath();

  const { orgSlug } = useParams<{
    orgSlug: string;
  }>();

  const navigateToIamWrite = useCallback(() => {
    navigateWithSearchParams(
      `/o/${orgSlug}/${WelcomePaths.Root}/${WelcomePaths.Onboarding}/${cloudProvider}/iam-write/${NEW_SENTINEL}`
    );
  }, [navigateWithSearchParams, orgSlug, cloudProvider]);

  const handleGenericInstall = useCallback(() => {
    if (!cloudProvider || !component) return;
    // The case is the previous step, the navigate is the next step
    // e.g after AWS iam-write is installed, ask the user if they
    //  want to install resource inventory
    if (cloudProvider === "aws") {
      switch (component) {
        case "iam-write":
          navigateWithSearchParams(
            buildWelcomePath(WelcomePaths.InstallResource)
          );
          break;
        case "iam-assessment":
          navigateWithSearchParams(
            buildWelcomePath(WelcomePaths.ConfigureApprovers)
          );
          break;
        case "inventory":
          navigateWithSearchParams(
            buildWelcomePath(WelcomePaths.ConfigureApprovers)
          );
          break;
        default:
          navigateWithSearchParams(buildWelcomePath(WelcomePaths.Root));
          break;
      }
    } else if (cloudProvider === "azure") {
      switch (component) {
        case "iam-write":
          navigateWithSearchParams(
            buildWelcomePath(WelcomePaths.ConfigureApprovers)
          );
          break;
        case "azure-app":
          navigateToIamWrite();
          break;
        default:
          navigateWithSearchParams(buildWelcomePath(WelcomePaths.Root));
          break;
      }
    } else if (cloudProvider === "gcloud") {
      switch (component) {
        case "iam-write-security-perimeter":
          navigateToIamWrite();
          break;
        case "iam-write":
        case "iam-assessment":
          navigateWithSearchParams(
            buildWelcomePath(WelcomePaths.ConfigureApprovers)
          );
          break;
        default:
          navigateWithSearchParams(buildWelcomePath(WelcomePaths.Root));
          break;
      }
    }
  }, [
    cloudProvider,
    component,
    navigateWithSearchParams,
    buildWelcomePath,
    navigateToIamWrite,
  ]);

  return (
    cloudProvider && (
      <Spacing mb={20}>
        <StyledCloudContainer>
          <ProviderComponent
            id={cloudProvider}
            cardOverride={cardOverride}
            componentOverride={component}
            onInstall={handleGenericInstall}
          />
        </StyledCloudContainer>
      </Spacing>
    )
  );
};
